import axios from 'axios';
import config from '../config';
import { getBearerToken } from '../util/cookie';
import { userDisplayNameAsString } from '../util/data';
import { fetchLivekitCredentials, buildJoinUrl } from './webrtc.duck';

// ================ Action types ================ //

export const CLIENTS_REQUEST = 'app/CoachSaaS/CLIENTS_REQUEST';
export const CLIENTS_SUCCESS = 'app/CoachSaaS/CLIENTS_SUCCESS';
export const CLIENTS_ERROR = 'app/CoachSaaS/CLIENTS_ERROR';

export const CURRENT_CLIENT_REQUEST = 'app/CoachSaaS/CURRENT_CLIENT_REQUEST';
export const CURRENT_CLIENT_SUCCESS = 'app/CoachSaaS/CURRENT_CLIENT_SUCCESS';
export const CURRENT_CLIENT_ERROR = 'app/CoachSaaS/CURRENT_CLIENT_ERROR';

export const CLIENT_PRIVATE_NOTE_REQUEST = 'app/CoachSaaS/CLIENT_PRIVATE_NOTE_REQUEST';
export const CLIENT_PRIVATE_NOTE_SUCCESS = 'app/CoachSaaS/CLIENT_PRIVATE_NOTE_SUCCESS';
export const CLIENT_PRIVATE_NOTE_ERROR = 'app/CoachSaaS/CLIENT_PRIVATE_NOTE_ERROR';

// ================ Reducer ================ //

const initialState = {
  clients: [],
  clientsError: null,
  clientsFetching: false,

  currentClient: null,
  currentClientError: null,
  currentClientFetching: false,

  clientPrivateNotes: null,
  clientPrivateNotesError: null,
  clientPrivateNotesFetching: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CLIENTS_REQUEST:
      return {
        ...state,
        clientsFetching: true,
      };
    case CLIENTS_SUCCESS:
      return {
        ...state,
        clientsFetching: false,
        clients: payload,
      };
    case CLIENTS_ERROR:
      return {
        ...state,
        clientsFetching: false,
        clientsError: payload,
      };


    case CURRENT_CLIENT_REQUEST:
      return {
        ...state,
        currentClientFetching: true,
      };
    case CURRENT_CLIENT_SUCCESS:
      return {
        ...state,
        currentClientFetching: false,
        currentClient: payload,
      };
    case CURRENT_CLIENT_ERROR:
      return {
        ...state,
        currentClientFetching: false,
        currentClientError: payload,
      };


    case CLIENT_PRIVATE_NOTE_REQUEST:
      return {
        ...state,
        clientPrivateNotesFetching: true,
      };
    case CLIENT_PRIVATE_NOTE_SUCCESS:
      return {
        ...state,
        clientPrivateNotesFetching: false,
        clientPrivateNotes: payload,
      };
    case CLIENT_PRIVATE_NOTE_ERROR:
      return {
        ...state,
        clientPrivateNotesFetching: false,
        clientPrivateNotesError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const clientsRequest = () => ({ type: CLIENTS_REQUEST });
export const clientsSuccess = data => ({ type: CLIENTS_SUCCESS, payload: data });
export const clientsError = error => ({ type: CLIENTS_ERROR, payload: error });

export const currentClientRequest = () => ({ type: CURRENT_CLIENT_REQUEST });
export const currentClientSuccess = data => ({ type: CURRENT_CLIENT_SUCCESS, payload: data });
export const currentClientError = error => ({ type: CURRENT_CLIENT_ERROR, payload: error });

export const clientPrivateNoteRequest = () => ({ type: CLIENT_PRIVATE_NOTE_REQUEST });
export const clientPrivateNoteSuccess = data => ({ type: CLIENT_PRIVATE_NOTE_SUCCESS, payload: data });
export const clientPrivateNoteError = error => ({ type: CLIENT_PRIVATE_NOTE_ERROR, payload: error });

// ================ Thunks ================ //

export const getAllClients = () => async (dispatch, getState, sdk) => {
  // MainDashboardWrapper (onboarding tour) and page-level components (e.g.
  // SaaSClientsPage) each call this independently on mount, firing two
  // concurrent requests against the same clientsFetching flag. If the
  // second one resolves after the first, its CLIENTS_REQUEST can flip
  // clientsFetching back to true even though the first request already
  // succeeded and populated `clients` — the page then shows real data
  // alongside a spinner that never clears. Skip if a fetch is already
  // in flight instead of letting them race.
  if (getState().clients.clientsFetching) return;

  dispatch(clientsRequest());

  // Whole request in one try/catch — including the bearer-token lookup.
  // The previous version only wrapped the axios call itself, so a
  // getBearerToken failure (expired/refresh race, etc.) threw before
  // axios.get was ever reached, skipping both clientsSuccess AND
  // clientsError. clientsFetching stayed true forever, i.e. the "no
  // clients yet" empty state never got a chance to render — the page
  // just showed an infinite spinner instead.
  //
  // That try/catch only protects against a REJECTED promise though —
  // getBearerToken's `await sdk.currentUser.show()` (see util/cookie.js)
  // has no timeout of its own, so if that call ever just hangs (stalled
  // request, unresponsive Flex API) instead of rejecting, nothing ever
  // throws and clientsFetching is stuck true forever with no way to
  // retry (the guard above skips any new call while fetching is true).
  // Race the whole request against a hard timeout so it always settles
  // one way or another.
  const timeout = ms =>
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('getAllClients timed out')), ms)
    );

  try {
    const res = await Promise.race([
      (async () => {
        const authHeader = await getBearerToken(sdk);
        return axios.get(`${config.coachSaaSService}clients`, {
          headers: {
            'content-type': 'application/json',
            Authorization: authHeader,
          },
          timeout: 15000,
        });
      })(),
      timeout(15000),
    ]);
    dispatch(clientsSuccess(res.data));
  } catch (err) {
    console.log(err);
    dispatch(clientsError(err));
  }
};

// Removes a client from the coach's roster. Only removes the coach-side
// record — it doesn't touch the client's account or past session
// history. Not available for v-corp sourced clients (no Firestore doc
// to delete; see access/clients/index.js removeClient).
export const deleteClient = (clientId) => async (dispatch, getState, sdk) => {
  return axios
    .delete(`${config.coachSaaSService}clients/${clientId}`, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    });
};

export const getClient = (clientId) => async (dispatch, getState, sdk) => {
  dispatch(currentClientRequest());
  // See getAllClients above — the bearer-token lookup must be inside the
  // try/catch too, or a token failure skips both success and error and
  // leaves currentClientFetching stuck true (infinite spinner on the
  // client detail page).
  try {
    const res = await axios.get(`${config.coachSaaSService}clients/${clientId}`, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    });
    dispatch(currentClientSuccess(res.data));
  } catch (err) {
    dispatch(currentClientError(err));
    console.log(err);
  }
};

export const getAllClientPrivateNotes = (clientId) => async (dispatch, getState, sdk) => {
  dispatch(clientPrivateNoteRequest());

  axios
    .get(`${config.coachSaaSService}clients/${clientId}/privatenotes`, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
    .then(res => {
      dispatch(clientPrivateNoteSuccess(res.data));
    })
    .catch(err => {
      console.log(err);
      dispatch(clientPrivateNoteError(err));
    });
};

export const updateClientPrivateNote = (clientId, noteId, sessionIndex, coachNote) => async (dispatch, getState, sdk) => {
  return axios
    .put(`${config.coachSaaSService}clients/${clientId}/privatenotes/${noteId}`, {
      sessionIndex,
      coachNote,
    }, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    });
};

export const addGeneralPrivateNote = (clientId, noteText) => async (dispatch, getState, sdk) => {
  return axios
    .post(`${config.coachSaaSService}clients/${clientId}/privatenotes/general`, {
      noteText,
    }, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    });
};

// Sends a client invitation. `email` is required; `options` (all
// optional) is the new richer payload that the friendlier Add Client
// form sends:
//   firstName, lastName     — used to greet the invitee in the email
//                             and label them in the pending list
//   inviteMessage           — short personal note from the coach,
//                             shown in a quoted block above the CTA
//   programId               — if set, the engagement (activity) is
//                             auto-created the moment the invitee
//                             accepts, so the coach skips the second
//                             "Assign a program" step
// Older call sites that pass just `email` keep working unchanged.
export const inviteClient = (email, options = {}) => async (dispatch, getState, sdk) => {
  return axios
    .post(`${config.coachSaaSService}clients/client-invitation/invite`,{
      email: email,
      firstName: options.firstName || undefined,
      lastName: options.lastName || undefined,
      inviteMessage: options.inviteMessage || undefined,
      programId: options.programId || undefined,
      // "Has the client already paid?" flow. When true the coach may
      // attach a program + a specific session; when false the coach
      // picks a marketplace listing to offer, surfaced in the invite
      // email so the client can pay/book to get started.
      hasPaid:
        typeof options.hasPaid === 'boolean' ? options.hasPaid : undefined,
      sessionId: options.sessionId || undefined,
      offeredListingId: options.offeredListingId || undefined,
      offeredListingTitle: options.offeredListingTitle || undefined,
      // Optional proposed first-session time { start, end, timezone, label }.
      // Booked when the client accepts (no-hold + re-validate-on-accept).
      firstMeeting: options.firstMeeting || undefined,
    }, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
    .catch(err => {
      // Wave 1 cap — backend returns 403 with structured payload when
      // the coach has hit their tier's client cap. Re-throw as a typed
      // error so the popup can show the CapReachedModal. Other errors
      // bubble up unchanged.
      const status = err && err.response && err.response.status;
      const body = err && err.response && err.response.data;
      if (status === 403 && body && body.error === 'tier-cap-reached') {
        const capErr = new Error('tier-cap-reached');
        capErr.tierCap = {
          feature: body.feature,
          tier: body.tier,
          limit: body.limit,
          current: body.current,
        };
        throw capErr;
      }
      throw err;
    });
};

/**
 * getMyOfferableListings
 *
 * Fetches the coach's own *published* marketplace listings so the
 * "Invite a client" popup can let the coach pick a listing to offer a
 * client who hasn't paid yet. Returns a lightweight array of
 * { id, title } — we deliberately don't touch the ManageListingsPage
 * redux slice so this can be called from anywhere without side effects.
 */
export const getMyOfferableListings = () => async (dispatch, getState, sdk) => {
  const res = await sdk.ownListings.query({
    // Only published listings are bookable/payable, so they're the only
    // ones worth offering. Drafts/closed listings are excluded.
    states: ['published'],
    per_page: 100,
  });

  const listings = (res && res.data && res.data.data) || [];
  return listings.map(l => ({
    id: l.id.uuid,
    title: (l.attributes && l.attributes.title) || 'Untitled listing',
  }));
};


// Verifies the signed "Join" link from a proposed-first-session invite email
// (see InvitationJoinPage) and resolves the LiveKit room to join. Auth-gated
// on the backend — the caller must be signed in, which the route already
// guarantees (auth: true in routeConfiguration sends a logged-out visitor
// through login/signup first).
export const resolveInvitationVideoJoin = ({ invitationId, providerId, jt }) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    const res = await axios.post(
      `${config.coachSaaSService}clients/client-invitation/video-call/resolve`,
      { invitationId, providerId, jt },
      {
        headers: {
          'content-type': 'application/json',
          Authorization: await getBearerToken(sdk),
        },
      }
    );
    return { ok: true, roomName: res.data && res.data.roomName };
  } catch (e) {
    return { ok: false };
  }
};

// Combines resolveInvitationVideoJoin (verify the signed link, get the
// LiveKit room) with the same credential-mint + join-URL-build steps the
// existing ad-hoc call flow uses (webrtc.duck.js's offer/answer), so
// InvitationJoinPage only has to dispatch one thunk and either open the
// meeting popup or show an error.
export const joinInvitationVideoCall = ({ invitationId, providerId, jt }) => async (
  dispatch,
  getState,
  sdk
) => {
  const resolved = await dispatch(resolveInvitationVideoJoin({ invitationId, providerId, jt }));
  if (!resolved.ok || !resolved.roomName) return { ok: false };

  const { currentUser } = getState().user || {};
  const myName = userDisplayNameAsString(currentUser, '');

  try {
    const { url, token } = await fetchLivekitCredentials(sdk, resolved.roomName, myName);
    return { ok: true, joinUrl: buildJoinUrl(resolved.roomName, token, url, myName) };
  } catch (e) {
    return { ok: false };
  }
};

// Read-only lookup so ClientInvitationAcceptPage can show the coach's offer
// with Accept/Not now buttons instead of accepting the moment the page
// loads. Anonymous-callable, same as accept below — the client may not
// have an account yet.
export const previewClientInvitation = (token) => async () => {
  return axios
    .post(`${config.coachSaaSService}clients/client-invitation/preview`,{
      invitationtoken: token
    }, {
      headers: {
        'content-type': 'application/json',
      },
    })
};

export const acceptClientInvitation = (token) => async (dispatch, getState, sdk) => {
  // Stays anonymous-callable (the client may not have an account yet on
  // first click), but if we're already authenticated — e.g. re-hitting
  // this after signing up with a different email than the coach invited —
  // attach a bearer token so the backend can bind the invitation to this
  // session's account instead of requiring an exact email match.
  const { isAuthenticated } = (getState().Auth || {});
  let authHeader;
  if (isAuthenticated) {
    try {
      authHeader = await getBearerToken(sdk);
    } catch (e) {
      // Couldn't get a fresh token — fall back to the anonymous accept
      // path (matches by invited email) rather than fail the request.
      authHeader = undefined;
    }
  }

  return axios
    .post(`${config.coachSaaSService}clients/client-invitation/accept`,{
      invitationtoken: token
    }, {
      headers: {
        'content-type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
    })
};


export const getAllPendingInvitations = () => async (dispatch, getState, sdk) => {
  return axios
    .post(`${config.coachSaaSService}clients/client-invitation/get-all`,{}, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
};

export const deleteClientInvitation = (id) => async (dispatch, getState, sdk) => {
  return axios
    .post(`${config.coachSaaSService}clients/client-invitation/delete`,{
      invitation_id: id
    }, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
};

export const resendClientInvitation = (id) => async (dispatch, getState, sdk) => {
  return axios
    .post(`${config.coachSaaSService}clients/client-invitation/resend`,{
      invitation_id: id
    }, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
};

// Counts pending sales transactions a coach needs to respond to. Includes
// BOTH transition states that count as "pending" in Viveka's flow:
//   - transition/enquire        — pure inquiry, no payment yet
//   - transition/confirm-payment — payment preauthorized, awaiting accept
//
// In Viveka's setup, when a client clicks "Inquire" on a listing they're
// actually charged immediately via Stripe, and the transaction lands in
// transition/confirm-payment state where the coach sees the Accept/Decline
// buttons on /sale/:id/details. So that state is the one that actually
// matters for the bell badge — earlier we only queried transition/enquire
// which always returned 0 because Viveka's flow skips that state.
//
// Powers:
//   - the bell badge in MainDashboardWrapper top nav
//   - the "Pending Enquiries" tile on /v-coach/clients
//   - the "Pending Enquiries" tile on /v-coach/sessions (Sessions)
//
// Returns a Promise resolving to a number (0 on error). Callers should
// store in component state and render a badge if > 0.
export const fetchPendingEnquiriesCount = () => async (dispatch, getState, sdk) => {
  try {
    const response = await sdk.transactions.query({
      only: 'sale',
      last_transitions: ['transition/enquire', 'transition/confirm-payment'],
      page: 1,
      per_page: 100,
    });
    const count = response?.data?.data?.length;
    return Number.isInteger(count) ? count : 0;
  } catch (err) {
    console.error('[pendingEnquiries] error:', err && err.message);
    return 0;
  }
};
